/*
 * <process-lightbox>: the media column of a process step with "Open photos full
 * size on click" (blocks/process-step.liquid). Each photo frame carries
 * data-full, its large rendition; this element turns those frames into buttons,
 * so without the script the photos stay plain images. A click opens the step's
 * dialog on that photo with its alt text and caption; until data-full is
 * decoded the photo already on the page stands in. Previous/next (buttons,
 * ←/→) move between the step's photos; Escape, the close button, the photo or
 * the scrim close it, and focus returns to the photo. Photos the theme editor
 * adds later are converted by the observer.
 */
if (!customElements.get('process-lightbox')) {
  customElements.define(
    'process-lightbox',
    class extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        if (!this.dialog || this.observer) return;
        this.image = this.dialog.querySelector('[data-lightbox-image]');
        this.caption = this.dialog.querySelector('[data-lightbox-caption]');
        this.index = 0;

        this.upgrade();
        this.observer = new MutationObserver(() => this.upgrade());
        this.observer.observe(this, { childList: true, subtree: true });

        this.addEventListener('click', (event) => {
          const photo = event.target.closest('button[data-full]');
          if (photo) this.open(photo);
        });
        this.dialog.addEventListener('click', (event) => {
          const arrow = event.target.closest('[data-lightbox-step]');
          if (arrow) return this.step(Number(arrow.dataset.lightboxStep));
          if (!event.target.closest('[data-lightbox-caption]')) this.close();
        });
        this.dialog.addEventListener('keydown', (event) => {
          if (event.altKey || event.ctrlKey || event.metaKey) return;
          if (event.key === 'ArrowRight') this.step(1);
          if (event.key === 'ArrowLeft') this.step(-1);
        });
        this.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.close();
        });
        this.dialog.addEventListener('close', () => {
          this.dialog.classList.remove('is-open');
          if (this.origin) this.origin.focus();
        });
      }

      photos() {
        return Array.from(this.querySelectorAll('button[data-full]'));
      }

      captionOf(photo) {
        const caption = photo.closest('figure')?.querySelector('figcaption');
        return caption ? caption.textContent.trim() : '';
      }

      /* The frame becomes a button with the same class; process-image__zoom adds the cursor and focus ring. */
      upgrade() {
        this.querySelectorAll('div[data-full]').forEach((frame) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = `${frame.className} process-image__zoom`;
          button.dataset.full = frame.dataset.full;
          const name = this.captionOf(frame) || frame.querySelector('img')?.alt;
          button.setAttribute('aria-label', name ? `${this.dataset.zoomLabel}: ${name}` : this.dataset.zoomLabel);
          button.append(...frame.childNodes);
          frame.replaceWith(button);
        });
      }

      open(photo) {
        const photos = this.photos();
        this.index = Math.max(0, photos.indexOf(photo));
        this.origin = photo;
        this.dialog.toggleAttribute('data-single', photos.length < 2);
        this.show();
        this.dialog.showModal();
        requestAnimationFrame(() => this.dialog.classList.add('is-open'));
      }

      show() {
        const photo = this.photos()[this.index];
        if (!photo) return;
        this.load(photo.dataset.full, photo.querySelector('img'));
        this.image.alt = photo.querySelector('img')?.alt || '';
        this.caption.textContent = this.captionOf(photo);
      }

      /* As in product-gallery.js: if the step's photo is already loaded on the page, it stands in until the
         full rendition is decoded, instead of an empty stage or the photo shown before. */
      load(full, preview) {
        const loader = new Image();
        loader.src = full;
        this.loading = loader;
        if (loader.complete || !preview?.complete || !preview.naturalWidth) {
          this.image.src = full;
          return;
        }
        this.image.src = preview.currentSrc;
        loader.decode().then(() => {
          if (this.loading === loader) this.image.src = full;
        }, () => {});
      }

      step(delta) {
        const count = this.photos().length;
        if (count < 2) return;
        this.index = (this.index + delta + count) % count;
        this.show();
      }

      close() {
        if (!this.dialog.open) return;
        this.dialog.classList.remove('is-open');
        const duration = parseFloat(getComputedStyle(this.dialog).transitionDuration) * 1000 || 0;
        setTimeout(() => this.dialog.close(), duration);
      }
    }
  );
}
